import type { GameId } from './games.js';

/**
 * Game settings owners choose (the game's mode, difficulty, world type…), as data: the API checks
 * them, the agent config carries them, each game's adapter applies them, and the UI draws its
 * controls from them. A new game brings its own list; nothing else names a setting.
 */
export type GameSettingValue = string | number | boolean;
export type GameSettings = Record<string, GameSettingValue>;

interface SettingBase {
  readonly key: string;
  readonly label: string;
  readonly help?: string;
  /**
   * `create`: chosen when the world is made, never after (the game reads it only then; none for an
   * uploaded world). `wake`: changed any time, applied from the next wake.
   */
  readonly when: 'create' | 'wake';
  /** Not offered yet: the UI hides it, the API refuses it, and the agent config leaves it out (the game's own default). */
  readonly hidden?: true;
  /** Another setting overrides this one: while `key` is `value`, the game uses `becomes` (Hardcore is always Hard). */
  readonly forcedBy?: { readonly key: string; readonly value: GameSettingValue; readonly becomes: GameSettingValue };
}

/** One choice; `note` is a short caution the UI shows beside it ("Best on Medium+"). */
export interface GameSettingOption {
  readonly value: string;
  readonly label: string;
  readonly note?: string;
}

export type GameSettingDef =
  | (SettingBase & { readonly kind: 'choice'; readonly options: readonly GameSettingOption[]; readonly default: string })
  | (SettingBase & { readonly kind: 'toggle'; readonly default: boolean })
  | (SettingBase & { readonly kind: 'number'; readonly min: number; readonly max: number; readonly default: number })
  | (SettingBase & { readonly kind: 'text'; readonly maxLength: number; readonly default: string });

/** The longest seed: Minecraft takes any text (a number is used as is, other text is hashed). */
export const SEED_MAX = 32;

/**
 * Minecraft's settings: each key maps to one server.properties line in the adapter (by version: the
 * world types are spelled differently before 1.19). Checked in Docker on 1.7.10–26.3 (2026-10-29).
 * PvP and mob spawning are game rules in recent versions: they come with game rules, as do the
 * Nether and command blocks there (hidden for now, owner, 2026-10-29). Flying is always allowed (the
 * adapter's, owner, 2026-10-30): not a choice.
 */
const MINECRAFT_SETTINGS: readonly GameSettingDef[] = [
  {
    key: 'mode',
    label: 'Game mode',
    kind: 'choice',
    options: [
      { value: 'survival', label: 'Survival' },
      { value: 'creative', label: 'Creative' },
      { value: 'adventure', label: 'Adventure' },
    ],
    default: 'survival',
    when: 'wake',
    help: 'Everyone plays in this mode.',
  },
  {
    key: 'difficulty',
    label: 'Difficulty',
    kind: 'choice',
    options: [
      { value: 'peaceful', label: 'Peaceful' },
      { value: 'easy', label: 'Easy' },
      { value: 'normal', label: 'Normal' },
      { value: 'hard', label: 'Hard' },
    ],
    default: 'normal',
    when: 'wake',
    forcedBy: { key: 'hardcore', value: true, becomes: 'hard' },
  },
  { key: 'hardcore', label: 'Hardcore', kind: 'toggle', default: false, when: 'create', help: "One life each, always on Hard. Can't be turned off later." },
  {
    key: 'worldType',
    label: 'World type',
    kind: 'choice',
    options: [
      { value: 'normal', label: 'Normal' },
      { value: 'flat', label: 'Flat' },
      { value: 'largeBiomes', label: 'Large biomes' },
      { value: 'amplified', label: 'Amplified', note: 'Best on Medium+' },
    ],
    default: 'normal',
    when: 'create',
  },
  { key: 'seed', label: 'Seed', kind: 'text', maxLength: SEED_MAX, default: '', when: 'create' },
  { key: 'structures', label: 'Generate Structures (Villages, Temples, etc.)', kind: 'toggle', default: true, when: 'create' },
  { key: 'nether', label: 'The Nether', kind: 'toggle', default: true, when: 'wake', hidden: true },
  { key: 'commandBlocks', label: 'Command blocks', kind: 'toggle', default: false, when: 'wake', hidden: true },
  { key: 'spawnProtection', label: 'Spawn protection', kind: 'number', min: 0, max: 16, default: 16, when: 'wake', hidden: true },
];

export const GAME_SETTINGS: Readonly<Record<GameId, readonly GameSettingDef[]>> = {
  'minecraft-java': MINECRAFT_SETTINGS,
};

/** The settings offered now: every one not hidden. */
export function visibleSettings(game: GameId): readonly GameSettingDef[] {
  return GAME_SETTINGS[game].filter((def) => !def.hidden);
}

/** The world settings' defaults (the `create` ones): stored in full for a world made here, so it can show them later. */
export function worldSettingDefaults(game: GameId): GameSettings {
  return Object.fromEntries(visibleSettings(game).filter((d) => d.when === 'create').map((d) => [d.key, d.default]));
}

/** No control characters (a seed or other text goes into a config file line). */
const PLAIN_TEXT = /^[^\p{Cc}]*$/u;

function valueProblem(def: GameSettingDef, value: GameSettingValue): string | undefined {
  switch (def.kind) {
    case 'choice':
      return def.options.some((option) => option.value === value) ? undefined : `${def.label} must be one of ${def.options.map((o) => o.value).join(', ')}`;
    case 'toggle':
      return typeof value === 'boolean' ? undefined : `${def.label} must be true or false`;
    case 'number':
      return Number.isInteger(value) && (value as number) >= def.min && (value as number) <= def.max
        ? undefined
        : `${def.label} must be a whole number from ${def.min} to ${def.max}`;
    case 'text':
      return typeof value === 'string' && value.length <= def.maxLength && PLAIN_TEXT.test(value)
        ? undefined
        : `${def.label} must be at most ${def.maxLength} characters, with no control characters`;
  }
}

export type ParsedGameSettings = { ok: true; value: GameSettings } | { ok: false; message: string };

/**
 * Checks settings someone asked for against the game's list: known and offered keys, values of the
 * right kind and range, and `create` settings only while a new world is being made (`newWorld`:
 * create without an upload; never for a change later).
 */
export function parseGameSettings(game: GameId, input: GameSettings, { newWorld }: { newWorld: boolean }): ParsedGameSettings {
  const defs = visibleSettings(game);
  for (const [key, value] of Object.entries(input)) {
    const def = defs.find((d) => d.key === key);
    if (!def) return { ok: false, message: `Unknown setting ${key}` };
    if (def.when === 'create' && !newWorld) return { ok: false, message: `${def.label} can only be chosen when a new world is made` };
    const problem = valueProblem(def, value);
    if (problem) return { ok: false, message: problem };
  }
  return { ok: true, value: { ...input } };
}

/**
 * Every offered setting's value: the stored choice, else the default, then any `forcedBy` applied.
 * What the agent config carries and the UI shows. Stored values for a setting since hidden or
 * removed are dropped, so the game falls back to its own default.
 */
export function resolveGameSettings(game: GameId, stored: GameSettings | undefined): GameSettings {
  const defs = visibleSettings(game);
  const resolved: GameSettings = {};
  for (const def of defs) {
    const value = stored?.[def.key];
    resolved[def.key] = value !== undefined && !valueProblem(def, value) ? value : def.default;
  }
  for (const def of defs) {
    if (def.forcedBy && resolved[def.forcedBy.key] === def.forcedBy.value) resolved[def.key] = def.forcedBy.becomes;
  }
  return resolved;
}
